import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { convertToTimezones } from '@/utils/timezone';
import { InfoIcon } from 'lucide-react';

interface TimezoneTooltipProps {
  /**
   * The UTC date to display timezone conversions for
   */
  utcDate: Date | string | number;
  /**
   * Optional className for the trigger button
   */
  className?: string;
  /**
   * Whether to show date only (no time)
   */
  dateOnly?: boolean;
}

/**
 * TimezoneTooltip displays a question mark icon that shows
 * the date/time in various UK and Canadian timezones when hovered
 */
export function TimezoneTooltip({ utcDate, className, dateOnly = false }: TimezoneTooltipProps) {
  const formatString = dateOnly ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm a zzz';
  const timezones = convertToTimezones(utcDate, formatString);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center justify-center ml-1 text-muted-foreground hover:text-foreground transition-colors ${className || ''}`}
            aria-label="View in different timezones"
          >
            <InfoIcon className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" align="start" className="max-w-sm">
          <div className="space-y-2">
            <div className="font-semibold text-sm border-b border-slate-700 pb-1">
              Timezone Conversions
            </div>
            <div className="space-y-1 text-xs">
              {timezones.map(tz => (
                <div key={tz.timezone} className="flex flex-col">
                  <span className="font-medium text-slate-300">{tz.label}</span>
                  <span className="text-slate-400">{tz.formatted}</span>
                </div>
              ))}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
